import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Select, MenuItem, FormControl, InputLabel, Button,
  LinearProgress, Chip, List, ListItem, ListItemText, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { sprintsAPI, tasksAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Sprint + Backlog management for a project.
 *  - Sprint selector + progress (story points)
 *  - Two-pane planning: backlog (no sprint) <-> selected sprint
 *  - Inline story-point editing
 *  - Sprint create/complete: internal users only (backend enforces too)
 */
function SprintPanel({ project }) {
  const projectId = project?.id;
  const { isClient } = useAuth();
  const clientUser = isClient && isClient();

  const [sprints, setSprints] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('');
  const [sprintData, setSprintData] = useState(null); // {sprint, tasks, statistics}
  const [backlog, setBacklog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const loadSprints = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await sprintsAPI.getAll(projectId, 'active');
      setSprints(res.data || []);
      if (res.data && res.data.length && !selectedSprint) {
        setSelectedSprint(res.data[0].id);
      }
    } catch (err) {
      console.error('[PM] sprint list error', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadBacklog = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await tasksAPI.getAll({ project_id: projectId });
      // Backlog = active, non-timetracker tasks not assigned to any sprint
      setBacklog((res.data || []).filter((t) => !t.sprint_id && !t.is_timetracker_only));
    } catch (err) {
      console.error('[PM] backlog error', err);
    }
  }, [projectId]);

  const loadSprintData = useCallback(async () => {
    if (!selectedSprint) { setSprintData(null); return; }
    setLoading(true);
    try {
      const res = await sprintsAPI.getTasks(selectedSprint);
      setSprintData(res.data);
    } catch (err) {
      console.error('[PM] sprint tasks error', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSprint]);

  useEffect(() => { loadSprints(); loadBacklog(); }, [loadSprints, loadBacklog]);
  useEffect(() => { loadSprintData(); }, [loadSprintData]);

  const refresh = () => { loadSprintData(); loadBacklog(); };

  const assignToSprint = async (task, sprintId) => {
    try {
      await tasksAPI.update(task.id, { sprint_id: sprintId });
      refresh();
    } catch (err) {
      setError('Konnte Task nicht zuordnen');
      console.error(err);
    }
  };

  const setEffort = async (task, value) => {
    const points = value === '' ? null : parseInt(value, 10);
    try {
      await tasksAPI.update(task.id, { effort_estimate: points });
      refresh();
    } catch (err) {
      console.error('[PM] effort update error', err);
    }
  };

  const completeSprint = async () => {
    if (!selectedSprint) return;
    try {
      await sprintsAPI.complete(selectedSprint);
      setSelectedSprint('');
      loadSprints();
      setSprintData(null);
    } catch (err) {
      setError('Sprint konnte nicht abgeschlossen werden');
    }
  };

  const stats = sprintData?.statistics;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel>Sprint</InputLabel>
          <Select value={selectedSprint} label="Sprint" onChange={(e) => setSelectedSprint(e.target.value)}>
            {sprints.length === 0 && <MenuItem value="" disabled>Kein aktiver Sprint</MenuItem>}
            {sprints.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {!clientUser && (
          <>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setCreateOpen(true)}>
              Sprint anlegen
            </Button>
            {selectedSprint && (
              <Button variant="outlined" color="success" onClick={completeSprint}>
                Sprint abschließen
              </Button>
            )}
          </>
        )}
      </Box>

      {/* Progress */}
      {stats && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {sprintData.sprint.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip size="small" label={`${stats.completed_tasks}/${stats.total_tasks} Tasks`} />
              <Chip size="small" color="primary"
                label={`${stats.completed_points}/${stats.total_points} SP`} />
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={stats.points_percentage}
            sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="caption" color="text.secondary">
            {stats.points_percentage}% der Story Points erledigt
          </Typography>
        </Paper>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}

      {/* Two-pane planning */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, minWidth: 280 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Backlog ({backlog.length})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {backlog.map((task) => (
              <ListItem
                key={task.id}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      type="number" size="small" placeholder="SP"
                      defaultValue={task.effort_estimate ?? ''}
                      onBlur={(e) => setEffort(task, e.target.value)}
                      sx={{ width: 64 }}
                      inputProps={{ min: 0 }}
                    />
                    {selectedSprint && (
                      <IconButton edge="end" title="In Sprint" onClick={() => assignToSprint(task, selectedSprint)}>
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
              >
                <ListItemText primary={task.name} secondary={task.task_id || null} />
              </ListItem>
            ))}
            {backlog.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Backlog ist leer
              </Typography>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, minWidth: 280 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Sprint ({sprintData?.tasks?.length || 0})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {(sprintData?.tasks || []).map((task) => (
              <ListItem
                key={task.id}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      type="number" size="small" placeholder="SP"
                      defaultValue={task.effort_estimate ?? ''}
                      onBlur={(e) => setEffort(task, e.target.value)}
                      sx={{ width: 64 }}
                      inputProps={{ min: 0 }}
                    />
                    <IconButton edge="end" title="Aus Sprint entfernen" onClick={() => assignToSprint(task, null)}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText primary={task.name} secondary={task.task_id || null} />
              </ListItem>
            ))}
            {(!sprintData || !sprintData.tasks || sprintData.tasks.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {selectedSprint ? 'Noch keine Tasks im Sprint' : 'Sprint auswählen'}
              </Typography>
            )}
          </List>
        </Paper>
      </Box>

      {!clientUser && (
        <CreateSprintDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          project={project}
          onCreated={() => { setCreateOpen(false); loadSprints(); }}
        />
      )}
    </Box>
  );
}

function CreateSprintDialog({ open, onClose, project, onCreated }) {
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', goal: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setForm({ name: '', start_date: '', end_date: '', goal: '' });
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      await sprintsAPI.create({ ...form, project_id: project.id });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Sprint konnte nicht erstellt werden');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sprint anlegen</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth margin="normal" label="Name (Bereich)" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} required
          placeholder="z. B. Datenmodellierung, Theming …" />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField margin="normal" label="Start (optional)" type="date" InputLabelProps={{ shrink: true }}
            value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} fullWidth />
          <TextField margin="normal" label="Ende (optional)" type="date" InputLabelProps={{ shrink: true }}
            value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} fullWidth />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Sprints können auch ohne Datum als reine Bereiche/Arbeitsstränge genutzt werden.
        </Typography>
        <TextField fullWidth margin="normal" label="Ziel (optional)" value={form.goal} multiline rows={2}
          onChange={(e) => setForm({ ...form, goal: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={submit}>Erstellen</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SprintPanel;
